import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useAuthStore } from '@/store/use-auth-store';
import { useCreateProcessFolder } from '@/hooks/process-folder/use-process-folder';
import { useTranslation } from 'react-i18next';

const processFolderSchema = z.object({
  name: z.string().min(1, 'Folder name is required'),
  description: z.string().optional(),
});

type ProcessFolderFormData = z.infer<typeof processFolderSchema>;

interface CreateProcessFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateProcessFolderModal: React.FC<CreateProcessFolderModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const createProcessFolderMutation = useCreateProcessFolder();

  const form = useForm<ProcessFolderFormData>({
    resolver: zodResolver(processFolderSchema),
    defaultValues: {
      name: '',
      description: '',
    },
  });

  const onSubmit = async (data: ProcessFolderFormData) => {
    if (!user) return;

    try {
      await createProcessFolderMutation.mutateAsync({
        name: data.name,
        description: data.description,
        creatorId: user.id,
      });
      form.reset();
      onClose();
    } catch (error) {
      console.error('Error creating process folder:', error);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('processManagement.addProcessFolder')}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('processManagement.folderName')}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={t('processManagement.enterFolderName')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('processManagement.folderDescription')}</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder={t('processManagement.enterFolderDescription')} rows={4} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={handleClose}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={createProcessFolderMutation.isPending}>
                {createProcessFolderMutation.isPending ? t('common.creating') : t('common.create')}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

